import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Search, Trash2, Edit, Upload, Tag, DollarSign, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PeriodFilter } from '../components/PeriodFilter';
import { Pagination } from '../components/shared/Pagination';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatNumber = (num) => {
  if (num === null || num === undefined) return '0.00';
  return Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const PAYMENT_METHODS = ['MoMo', 'Equity Bank', 'Bank of Kigali', 'Cash'];

const ExpensesPage = () => {
  const { hasPermission } = useAuth();
  const canDelete = hasPermission('delete_expense');
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [periodFilter, setPeriodFilter] = useState({ startDate: '', endDate: '', label: '' });
  const [customerFilter, setCustomerFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentExpense, setPaymentExpense] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] });
  const [expensePayments, setExpensePayments] = useState({ payments: [], total_paid: 0, balance_due: 0, total_amount: 0 });
  const [editingPayment, setEditingPayment] = useState(null);
  
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    amount: '',
    customer_id: '',
    vat_inclusive: false,
    date: new Date().toISOString().split('T')[0]
  });
  const [file, setFile] = useState(null);

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API_URL}/expenses`);
      setExpenses(response.data);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/expenses/categories`);
      setCategories(response.data);
      if (response.data.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: response.data[0] }));
      }
    } catch (error) {
      // Fallback to default categories
      setCategories(['Rent', 'Transport', 'Salaries', 'Utilities', 'Marketing', 'Other']);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/expenses/categories?name=${encodeURIComponent(newCategory)}`);
      toast.success('Category created');
      setNewCategory('');
      setCategoryDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file && !editingExpense) {
      toast.error('Please attach a supporting document');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('category', formData.category);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('amount', formData.amount);
    formDataToSend.append('date', formData.date);
    formDataToSend.append('vat_inclusive', formData.vat_inclusive ? 'true' : 'false');
    if (formData.customer_id) {
      formDataToSend.append('customer_id', formData.customer_id);
    }
    
    if (file) {
      formDataToSend.append('file', file);
    }

    try {
      if (editingExpense) {
        await axios.put(`${API_URL}/expenses/${editingExpense.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Expense updated successfully');
      } else {
        await axios.post(`${API_URL}/expenses`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Expense recorded successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const resetForm = () => {
    setFormData({
      category: categories[0] || 'Other',
      description: '',
      amount: '',
      customer_id: '',
      vat_inclusive: false,
      date: new Date().toISOString().split('T')[0]
    });
    setFile(null);
    setEditingExpense(null);
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category || '',
      description: expense.description || '',
      amount: expense.amount?.toString() || '',
      customer_id: expense.customer_id || '',
      vat_inclusive: expense.vat_inclusive || false,
      date: expense.date?.split('T')[0] || new Date().toISOString().split('T')[0]
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await axios.delete(`${API_URL}/expenses/${id}`);
      toast.success('Expense deleted successfully');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} expenses?`)) return;
    
    try {
      await axios.delete(`${API_URL}/expenses`, { data: selectedIds });
      toast.success(`Deleted ${selectedIds.length} expenses`);
      setSelectedIds([]);
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expenses');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredExpenses.map(e => e.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = (expense.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (expense.category || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCustomer = customerFilter === 'all' || expense.customer_id === customerFilter;
    
    const expenseDate = new Date(expense.created_at);
    const matchesDateFrom = !periodFilter.startDate || expenseDate >= new Date(periodFilter.startDate);
    const matchesDateTo = !periodFilter.endDate || expenseDate <= new Date(periodFilter.endDate + 'T23:59:59');
    
    return matchesSearch && matchesCustomer && matchesDateFrom && matchesDateTo;
  });

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;
  const totalPages = Math.ceil(filteredExpenses.length / ITEMS_PER_PAGE) || 1;
  const safePage = Math.min(currentPage, totalPages);
  const paginatedExpenses = filteredExpenses.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const totalExpenses = expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);

  // Payment functions
  const openPaymentDialog = async (expense) => {
    setPaymentExpense(expense);
    setPaymentForm({ amount: '', payment_method: expense.payment_method || 'Cash', payment_date: new Date().toISOString().split('T')[0] });
    setEditingPayment(null);
    try {
      const response = await axios.get(`${API_URL}/expenses/${expense.id}/payments`);
      setExpensePayments(response.data);
    } catch (error) {
      setExpensePayments({ payments: [], total_paid: 0, balance_due: expense.amount, total_amount: expense.amount });
    }
    setPaymentDialogOpen(true);
  };

  const fetchPaymentHistory = async () => {
    if (!paymentExpense) return;
    try {
      const response = await axios.get(`${API_URL}/expenses/${paymentExpense.id}/payments`);
      setExpensePayments(response.data);
    } catch (error) {
      console.error('Failed to fetch payment history');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentExpense || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append('payment_method', paymentForm.payment_method);
      params.append('amount', paymentForm.amount);
      if (paymentForm.payment_date) params.append('payment_date', paymentForm.payment_date);
      
      await axios.post(`${API_URL}/expenses/${paymentExpense.id}/payment?${params.toString()}`);
      toast.success('Payment recorded successfully');
      setPaymentForm({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] });
      await fetchPaymentHistory();
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({ amount: payment.amount.toString(), payment_method: payment.payment_method, payment_date: (payment.date || payment.created_at || '').split('T')[0] || new Date().toISOString().split('T')[0] });
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append('amount', paymentForm.amount);
      params.append('payment_method', paymentForm.payment_method);
      if (paymentForm.payment_date) params.append('payment_date', paymentForm.payment_date);
      
      await axios.put(`${API_URL}/expenses/${paymentExpense.id}/payments/${editingPayment.id}?${params.toString()}`);
      toast.success('Payment updated successfully');
      setEditingPayment(null);
      setPaymentForm({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] });
      await fetchPaymentHistory();
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    try {
      await axios.delete(`${API_URL}/expenses/${paymentExpense.id}/payments/${paymentId}`);
      toast.success('Payment deleted successfully');
      await fetchPaymentHistory();
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    }
  };

  const cancelEditPayment = () => {
    setEditingPayment(null);
    setPaymentForm({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">Expenses</h1>
          <p className="text-muted-foreground">Track all business expenses</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCategoryDialogOpen(true)} data-testid="manage-categories-btn">
            <Tag className="w-4 h-4 mr-2" />
            Categories
          </Button>
          {selectedIds.length > 0 && canDelete && (
            <Button variant="destructive" onClick={handleDeleteSelected} data-testid="delete-selected-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedIds.length})
            </Button>
          )}
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" 
            data-testid="add-expense-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Period Filter */}
      <PeriodFilter onFilterChange={setPeriodFilter} />

      <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
          <div className="flex items-center space-x-2 flex-1">
            <Search className="w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search expenses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
              data-testid="expense-search-input"
            />
          </div>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger className="w-full md:w-[220px]" data-testid="expenses-customer-filter">
              <SelectValue placeholder="Filter by customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {customers.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-lg font-semibold text-foreground">
          Total Expenses: <span className="text-red-600">RWF {formatNumber(filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0))}</span>
          {periodFilter.label && <span className="text-sm text-muted-foreground font-normal ml-2">({periodFilter.label})</span>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="expenses-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-4 text-left">
                  <Checkbox 
                    checked={selectedIds.length === filteredExpenses.length && filteredExpenses.length > 0}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Date</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Category</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Description</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Payment</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Amount</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Paid</th>
                <th className="px-4 py-4 text-left text-sm font-medium text-foreground">Balance</th>
                <th className="px-4 py-4 text-center text-sm font-medium text-foreground">Doc</th>
                <th className="px-4 py-4 text-right text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredExpenses.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-muted-foreground">
                    No expenses recorded yet. Add your first expense to get started.
                  </td>
                </tr>
              )}
              {paginatedExpenses.map((expense) => (
                <tr key={expense.id} className="hover:bg-muted/30 transition-colors" data-testid="expense-row">
                  <td className="px-4 py-4">
                    <Checkbox 
                      checked={selectedIds.includes(expense.id)}
                      onCheckedChange={(checked) => handleSelectOne(expense.id, checked)}
                    />
                  </td>
                  <td className="px-4 py-4 text-sm text-muted-foreground">
                    {new Date(expense.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4 text-sm">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {expense.category}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-muted-foreground max-w-[200px] truncate">{expense.description}</td>
                  <td className="px-4 py-4 text-sm">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {expense.payment_method}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm font-semibold text-red-600">
                    RWF {formatNumber(expense.amount)}
                  </td>
                  <td className="px-4 py-4 text-sm text-emerald-600">
                    {formatNumber(expense.amount_paid || 0)}
                  </td>
                  <td className="px-4 py-4 text-sm">
                    {(expense.balance_due || (expense.amount - (expense.amount_paid || 0))) > 0 ? (
                      <span className="text-amber-600 font-medium">RWF {formatNumber(expense.balance_due || (expense.amount - (expense.amount_paid || 0)))}</span>
                    ) : (
                      <span className="text-emerald-600">Paid</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-center">
                    {expense.supporting_document_filename ? (
                      <span className="text-xs text-green-600">✓</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openPaymentDialog(expense)} data-testid="record-expense-payment-btn" title="Record Payment">
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                      </Button>
                      {expense.supporting_document_filename && (
                        <Button variant="ghost" size="sm" onClick={async () => {
                          try {
                            const response = await axios.get(`${API_URL}/expenses/document/${expense.id}`, { responseType: 'blob' });
                            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = expense.supporting_document_filename || 'document.pdf';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                          } catch (error) { toast.error('Failed to download document'); }
                        }} data-testid="download-expense-doc-btn" title="Download Document">
                          <Download className="w-4 h-4 text-blue-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(expense)} data-testid="edit-expense-btn">
                        <Edit className="w-4 h-4" />
                      </Button>
                      {canDelete && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(expense.id)} data-testid="delete-expense-btn">
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={currentPage} pages={totalPages} total={filteredExpenses.length} limit={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
      </div>

      {/* Expense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Record New Expense'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Category *</label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger data-testid="expense-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description *</label>
              <Textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the expense..."
                data-testid="expense-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Amount (RWF) *</label>
                <Input
                  type="number"
                  step="0.01"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  data-testid="expense-amount-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date *</label>
                <Input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="expense-date-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Link to Customer (Optional - for COGS tracking)</label>
              <Select 
                value={formData.customer_id || 'none'} 
                onValueChange={(value) => setFormData({ ...formData, customer_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger data-testid="expense-customer-select">
                  <SelectValue placeholder="General Expense (No Customer)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">General Expense (No Customer)</SelectItem>
                  {customers.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Link to customer to include in their COGS calculation
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Supporting Document {!editingExpense && '*'}
              </label>
              <div className="flex items-center space-x-2">
                <Input
                  type="file"
                  required={!editingExpense}
                  onChange={(e) => setFile(e.target.files[0])}
                  accept=".pdf,.jpg,.jpeg,.png"
                  data-testid="expense-file-input"
                />
                <Upload className="w-5 h-5 text-muted-foreground" />
              </div>
              {file && <p className="text-xs text-muted-foreground mt-1">{file.name}</p>}
              {editingExpense && !file && (
                <p className="text-xs text-muted-foreground mt-1">Leave empty to keep existing document</p>
              )}
            </div>
            <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <Checkbox
                id="expense-vat-inclusive"
                checked={formData.vat_inclusive}
                onCheckedChange={(checked) => setFormData({ ...formData, vat_inclusive: checked })}
                data-testid="expense-vat-inclusive"
              />
              <label htmlFor="expense-vat-inclusive" className="text-sm font-medium leading-none cursor-pointer">
                VAT Inclusive (18% - Category B)
              </label>
              <span className="text-xs text-muted-foreground ml-auto">Check if expense includes 18% VAT</span>
            </div>
            <Button type="submit" className="w-full" data-testid="expense-submit-btn">
              {editingExpense ? 'Update Expense' : 'Record Expense'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>Manage Expense Categories</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Create New Category</label>
              <div className="flex gap-2">
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Category name"
                  data-testid="new-category-input"
                />
                <Button onClick={handleCreateCategory} data-testid="create-category-btn">
                  Add
                </Button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Existing Categories</label>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <span 
                    key={cat} 
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-muted"
                  >
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={(open) => {
        setPaymentDialogOpen(open);
        if (!open) {
          setEditingPayment(null);
          setPaymentForm({ amount: '', payment_method: 'Cash', payment_date: new Date().toISOString().split('T')[0] });
        }
      }}>
        <DialogContent className="max-w-md" data-testid="expense-payment-dialog">
          <DialogHeader>
            <DialogTitle>{editingPayment ? 'Edit Payment' : 'Record Expense Payment'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Expense Info */}
            <div className="bg-muted/30 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Category:</span>
                <span className="font-medium">{paymentExpense?.category}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Description:</span>
                <span className="font-medium truncate max-w-[200px]">{paymentExpense?.description}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Amount:</span>
                <span className="font-semibold text-red-600">RWF {formatNumber(expensePayments.total_amount || paymentExpense?.amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Amount Paid:</span>
                <span className="text-emerald-600 font-medium">RWF {formatNumber(expensePayments.total_paid)}</span>
              </div>
              <div className="flex justify-between text-sm border-t pt-2 mt-2">
                <span className="font-medium">Balance Due:</span>
                <span className="text-amber-600 font-bold">
                  RWF {formatNumber(expensePayments.balance_due)}
                </span>
              </div>
            </div>

            {/* Payment History */}
            {expensePayments.payments?.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Payment History</label>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                  {expensePayments.payments.map((payment) => (
                    <div key={payment.id} className="flex justify-between items-center text-xs bg-muted/20 p-2 rounded">
                      <div>
                        <span>{new Date(payment.created_at).toLocaleDateString()}</span>
                        <span className="mx-2">-</span>
                        <span>{payment.payment_method}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">RWF {formatNumber(payment.amount)}</span>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEditPayment(payment)}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDeletePayment(payment.id)}>
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Form */}
            {(expensePayments.balance_due > 0 || editingPayment) && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {editingPayment ? 'Edit Payment Amount (RWF)' : 'Payment Amount (RWF)'}
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder={editingPayment ? 'Enter amount' : `Max: ${formatNumber(expensePayments.balance_due)}`}
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    data-testid="expense-payment-amount"
                  />
                  {!editingPayment && expensePayments.balance_due > 0 && (
                    <div className="flex gap-2 mt-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => setPaymentForm({ 
                          ...paymentForm, 
                          amount: expensePayments.balance_due.toString() 
                        })}
                      >
                        Pay Full Balance
                      </Button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Payment Method</label>
                  <Select 
                    value={paymentForm.payment_method} 
                    onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
                  >
                    <SelectTrigger data-testid="expense-payment-method">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(method => (
                        <SelectItem key={method} value={method}>{method}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Payment Date</label>
                  <Input
                    type="date"
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                    data-testid="expense-payment-date"
                  />
                </div>

                {editingPayment ? (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={cancelEditPayment} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={handleUpdatePayment} className="flex-1" data-testid="expense-update-payment-btn">
                      <Edit className="w-4 h-4 mr-2" />
                      Update Payment
                    </Button>
                  </div>
                ) : (
                  <Button onClick={handleRecordPayment} className="w-full" data-testid="expense-submit-payment-btn">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Record Payment
                  </Button>
                )}
              </>
            )}

            {expensePayments.balance_due <= 0 && !editingPayment && (
              <div className="text-center py-3 mb-3 bg-emerald-50 rounded-lg">
                <div className="text-emerald-600 font-semibold">Fully Paid</div>
                <p className="text-xs text-muted-foreground mt-1">Click on a payment above to edit it</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
